import React, { useState } from 'react';
import { trpc } from '@/lib/trpc';
import { WorkoutTemplate, PersonalRecord, Exercise } from '@/lib/types';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Activity,
  Edit2,
  Flame,
  Heart,
  LineChart,
  Trash2,
  ClipboardList,
} from 'lucide-react';
import { toast } from 'sonner';
import EditCompletedSessionDialog from './EditCompletedSessionDialog';
import ExerciseProgressionDialog from './ExerciseProgressionDialog';
import TelemetryMesocycleMode from './TelemetryMesocycleMode';
import TelemetryLegacyMode from './TelemetryLegacyMode';

interface PRsAndTemplatesProps {
  onLoadTemplate?: (template: WorkoutTemplate) => void;
  refreshTrigger: number;
  clientId?: number; // When set, show client's data (trainer view — read-only)
}

type TelemetryMode = 'mesocycle' | 'legacy';

export default function PRsAndTemplates({ onLoadTemplate, refreshTrigger, clientId }: PRsAndTemplatesProps) {
  const isClientView = !!clientId;

  const [telemetryMode, setTelemetryMode] = useState<TelemetryMode>('mesocycle');

  const [expandedSessionId, setExpandedSessionId] = useState<string | null>(null);
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
  const [isEditOpen, setIsEditOpen] = useState(false);

  // Progression graph dialog state
  const [progressionExerciseId, setProgressionExerciseId] = useState<string | null>(null);
  const [progressionExerciseName, setProgressionExerciseName] = useState('');
  const [progressionInitialMode, setProgressionInitialMode] = useState<'overload' | 'hypertrophy'>('overload');

  const utils = trpc.useUtils();

  const { data: rawExercises = [] } = trpc.exercises.list.useQuery();
  const { data: ownSessions = [] } = trpc.sessions.list.useQuery(undefined, { enabled: !isClientView });
  const { data: clientSessions = [] } = trpc.trainerPortal.getClientSessions.useQuery(
    { clientId: clientId! },
    { enabled: isClientView && !!clientId }
  );
  const allSessions = isClientView ? clientSessions : ownSessions;

  const exercises: Exercise[] = rawExercises.map((e) => ({
    id: e.id,
    name: e.name,
    category: e.category,
    notes: e.notes ?? undefined,
    isCustom: e.isCustom,
    trackingMode: e.trackingMode,
    defaultTimerSeconds: e.defaultTimerSeconds ?? undefined,
  }));

  const completedSessions = allSessions
    .filter((s) => !!s.completedAt)
    .sort((a, b) => (b.completedAt ?? 0) - (a.completedAt ?? 0));

  const deleteSessionMutation = trpc.sessions.delete.useMutation({
    onSuccess: () => {
      utils.sessions.list.invalidate();
      utils.sessions.byDate.invalidate();
      utils.prs.list.invalidate();
    },
  });

  const handleDeleteSession = async (id: string, name: string) => {
    if (confirm(`Are you sure you want to delete the completed session "${name}"? This will permanently remove its metrics and associated PRs.`)) {
      await deleteSessionMutation.mutateAsync({ sessionId: id });
      toast.success(`Deleted session "${name}"`);
    }
  };

  const formatDuration = (totalSecs: number) => {
    const m = Math.floor(totalSecs / 60);
    const s = totalSecs % 60;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  };

  return (
    <div className="space-y-5">

      {/* ── Mode Toggle ────────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-1 bg-secondary/40 border border-border/50 rounded-sm p-1 w-fit">
        <button
          onClick={() => setTelemetryMode('mesocycle')}
          className={`px-4 py-1.5 text-xs font-mono font-bold uppercase tracking-wider rounded-sm transition-all duration-200 ${
            telemetryMode === 'mesocycle'
              ? 'bg-primary text-primary-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          Mesocycle
        </button>
        <button
          onClick={() => setTelemetryMode('legacy')}
          className={`px-4 py-1.5 text-xs font-mono font-bold uppercase tracking-wider rounded-sm transition-all duration-200 ${
            telemetryMode === 'legacy'
              ? 'bg-primary text-primary-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          Legacy
        </button>
      </div>

      {/* ── Mode Content ───────────────────────────────────────────────────────── */}
      <div
        key={telemetryMode}
        className="animate-in fade-in duration-200"
      >
        {telemetryMode === 'mesocycle' ? (
          <TelemetryMesocycleMode
            refreshTrigger={refreshTrigger}
            onLoadTemplate={onLoadTemplate}
          />
        ) : (
          <TelemetryLegacyMode refreshTrigger={refreshTrigger} />
        )}
      </div>

      {/* ── Completed Workout History (always visible, below the mode panels) ─── */}
      {!isClientView && (
        <div className="forge-panel p-4 space-y-4">
          <div className="forge-header">
            <span>Completed Workout History</span>
            <span className="text-primary font-mono flex items-center gap-1">
              <ClipboardList className="h-3.5 w-3.5" /> LOG ARCHIVE
            </span>
          </div>

          <div className="text-xs font-mono text-muted-foreground uppercase">Session History</div>

          {completedSessions.length === 0 ? (
            <div className="text-center py-8 border border-dashed border-border/50 rounded-sm">
              <Activity className="h-8 w-8 text-muted-foreground/20 mx-auto mb-3" />
              <p className="text-xs font-mono text-muted-foreground uppercase">No Completed Workouts Yet</p>
              <p className="text-[10px] text-muted-foreground/60 mt-1">Finish a live session to see your history here</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
              {completedSessions.map((session) => {
                const isExpanded = expandedSessionId === session.id;

                let totalTonnage = 0;
                let totalSetsCount = 0;
                let totalDurationSeconds = 0;

                const plannedExercises = session.plannedExercises as any[];
                plannedExercises.forEach((pe: any) => {
                  const ex = exercises.find((e) => e.id === pe.exerciseId);
                  const isTimeBased = ex?.trackingMode === 'time';
                  (pe.sets as any[]).forEach((set: any) => {
                    if (set.completed) {
                      totalSetsCount++;
                      if (isTimeBased) {
                        totalDurationSeconds += set.durationSeconds || 0;
                      } else {
                        totalTonnage += (set.weight || 0) * (set.reps || 0);
                      }
                    }
                  });
                });

                return (
                  <div key={session.id} className="border border-border/50 rounded-sm overflow-hidden bg-secondary/10 hover:bg-secondary/20 transition-all">
                    <div className="p-3 flex items-center justify-between cursor-pointer" onClick={() => setExpandedSessionId(isExpanded ? null : session.id)}>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-sm text-foreground">{session.name}</span>
                          <span className="text-[9px] font-mono bg-primary/10 text-primary px-1.5 py-0.5 rounded-sm uppercase">{session.date}</span>
                        </div>
                        <div className="text-[10px] font-mono text-muted-foreground uppercase mt-1 flex flex-wrap gap-x-2">
                          <span>{plannedExercises.length} Movements</span>
                          <span>•</span>
                          <span>{totalSetsCount} Sets</span>
                          {totalTonnage > 0 && (
                            <>
                              <span>•</span>
                              <span>{totalTonnage.toLocaleString()} LBS</span>
                            </>
                          )}
                          {totalDurationSeconds > 0 && (
                            <>
                              <span>•</span>
                              <span>{formatDuration(totalDurationSeconds)} Timed</span>
                            </>
                          )}
                        </div>
                        {/* Watch biometrics row */}
                        {((session as any).watchCalories || (session as any).watchAvgHr) && (
                          <div className="flex items-center gap-3 mt-1">
                            {(session as any).watchCalories && (
                              <span className="flex items-center gap-1 text-[9px] font-mono text-orange-400">
                                <Flame className="h-3 w-3" />
                                {(session as any).watchCalories} CAL
                              </span>
                            )}
                            {(session as any).watchAvgHr && (
                              <span className="flex items-center gap-1 text-[9px] font-mono text-red-400">
                                <Heart className="h-3 w-3" />
                                {(session as any).watchAvgHr}{(session as any).watchMaxHr ? `/${(session as any).watchMaxHr}` : ''} BPM
                              </span>
                            )}
                            <span className="text-[9px] font-mono text-muted-foreground/50 flex items-center gap-1">
                              <Activity className="h-2.5 w-2.5" />
                              WATCH
                            </span>
                          </div>
                        )}
                      </div>

                      <div className="flex items-center gap-1 shrink-0 ml-2" onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="sm" onClick={() => setExpandedSessionId(isExpanded ? null : session.id)} className="h-7 px-2 font-mono text-[10px] text-primary">
                          {isExpanded ? 'HIDE' : 'VIEW'}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setEditingSessionId(session.id);
                            setIsEditOpen(true);
                          }}
                          className="h-7 w-7 text-muted-foreground hover:text-primary"
                          title="Edit Session"
                        >
                          <Edit2 className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteSession(session.id, session.name)}
                          className="h-7 w-7 text-muted-foreground hover:text-destructive"
                          title="Delete Session"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="px-3 pb-3 border-t border-border/30 bg-background/40 space-y-3 pt-2.5">
                        {session.notes && (
                          <div className="p-2 bg-secondary/30 rounded-sm border border-border/40 text-xs italic text-muted-foreground">
                            <strong>Session Notes:</strong> {session.notes}
                          </div>
                        )}
                        <div className="space-y-2">
                          {plannedExercises.map((pe: any) => {
                            const ex = exercises.find((e) => e.id === pe.exerciseId);
                            const completedSets = (pe.sets as any[]).filter((s: any) => s.completed);
                            return (
                              <div key={pe.id} className="p-2 bg-secondary/10 border border-border/30 rounded-sm space-y-1">
                                <div className="flex items-center justify-between">
                                  <span className="font-bold text-xs text-primary">{ex?.name || 'Unknown Lift'}</span>
                                  <Badge variant="outline" className="text-[8px] font-mono uppercase bg-background border-border">
                                    {ex?.category}
                                  </Badge>
                                </div>
                                <div className="grid grid-cols-4 sm:grid-cols-6 gap-1.5 pt-1">
                                  {completedSets.map((set: any, sIdx: number) => (
                                    <div key={set.id} className="p-1 bg-background/50 border border-border/50 rounded-sm text-center">
                                      <div className="text-[8px] font-mono text-muted-foreground uppercase">SET {sIdx + 1}</div>
                                      {ex?.trackingMode === 'time' ? (
                                        <>
                                          <div className="font-mono text-[10px] font-bold mt-0.5">
                                            {set.durationSeconds ?? 0} <span className="text-[8px] font-normal text-muted-foreground">SECS</span>
                                          </div>
                                          <div className="font-mono text-[9px] text-primary font-bold">
                                            {set.reps ?? 0} <span className="text-[8px] font-normal text-muted-foreground">REPS</span>
                                          </div>
                                        </>
                                      ) : (
                                        <>
                                          <div className="font-mono text-[10px] font-bold mt-0.5">
                                            {set.weight ?? 0} <span className="text-[8px] font-normal text-muted-foreground">LBS</span>
                                          </div>
                                          <div className="font-mono text-[9px] text-primary font-bold">
                                            {set.reps ?? 0} <span className="text-[8px] font-normal text-muted-foreground">REPS</span>
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  ))}
                                  {completedSets.length === 0 && (
                                    <span className="text-[10px] font-mono text-muted-foreground uppercase col-span-full italic">No completed sets</span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Edit Completed Session Dialog */}
      <EditCompletedSessionDialog
        sessionId={editingSessionId}
        isOpen={isEditOpen}
        onClose={() => {
          setIsEditOpen(false);
          setEditingSessionId(null);
        }}
        onSave={() => {
          utils.sessions.list.invalidate();
          utils.sessions.byDate.invalidate();
          utils.prs.list.invalidate();
        }}
      />

      {/* Exercise Progression Graph Dialog */}
      <ExerciseProgressionDialog
        exerciseId={progressionExerciseId}
        exerciseName={progressionExerciseName}
        initialMode={progressionInitialMode}
        onClose={() => setProgressionExerciseId(null)}
      />
    </div>
  );
}
